import { useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import { EmblaCarouselType } from 'embla-carousel'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from './PreviewCarouselArrows.module.scss'

export const PREVIEW_CAROUSEL_HOVER_TARGET = 'preview-carousel-hover-target'

interface Props {
  emblaApi: EmblaCarouselType | null | undefined
}

export const PreviewCarouselArrows = ({ emblaApi }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const syncScrollState = useCallback(() => {
    if (!emblaApi) return
    const nextCanScrollPrev = emblaApi.canScrollPrev()
    const nextCanScrollNext = emblaApi.canScrollNext()
    setCanScrollPrev((prev) => (prev === nextCanScrollPrev ? prev : nextCanScrollPrev))
    setCanScrollNext((prev) => (prev === nextCanScrollNext ? prev : nextCanScrollNext))
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    syncScrollState()
    emblaApi.on('select', syncScrollState)
    emblaApi.on('reInit', syncScrollState)
    return () => {
      emblaApi.off('select', syncScrollState)
      emblaApi.off('reInit', syncScrollState)
    }
  }, [emblaApi, syncScrollState])

  const handlePrevClick = () => emblaApi?.scrollPrev()
  const handleNextClick = () => emblaApi?.scrollNext()

  if (!emblaApi || isMobile) return null

  return (
    <>
      <span className={cx(Styles.arrow, Styles.prev)} data-preview-carousel-arrow data-disabled={!canScrollPrev}>
        <IconButton
          rounded
          variant="secondary"
          size="md"
          icon={ChevronLeft}
          title="Previous slide"
          isDisabled={!canScrollPrev}
          onPress={handlePrevClick}
        />
      </span>
      <span className={cx(Styles.arrow, Styles.next)} data-preview-carousel-arrow data-disabled={!canScrollNext}>
        <IconButton
          rounded
          variant="secondary"
          size="md"
          icon={ChevronRight}
          title="Next slide"
          isDisabled={!canScrollNext}
          onPress={handleNextClick}
        />
      </span>
    </>
  )
}
